<template>
    <v-container fluid class="form-content overflow-auto pa-5">
        <v-form ref="form" :disabled="!writable" @input="$emit('input', $event)">
            <h4 class="mb-2 mt-5">{{ $t('infos') }}</h4>
            <!-- Legal Type -->
            <v-row dense>
                <v-col cols="12">
                    <v-radio-group
                        dense
                        :value="addressToEdit?.legal_type"
                        :rules="[rules.required]"
                        row
                        @change="addressToEdit?.set('legal_type', $event)"
                    >
                        <v-radio value="person">
                            <template #label>
                                <KpIcon icon-cls="person" color="grey" />
                                <span>{{ $t('person_label') }}</span>
                            </template>
                        </v-radio>

                        <v-radio value="organization">
                            <template #label>
                                <KpIcon icon-cls="institution" color="grey" />
                                <span>{{ $t('organization_label') }}</span>
                            </template>
                        </v-radio>
                    </v-radio-group>
                </v-col>
            </v-row>

            <!-- Bezeichnung -->
            <v-row dense>
                <v-col cols="12" class="d-flex align-center" style="gap: 5px">
                    <LabelField
                        class="flex-grow-1"
                        :label="addressToEdit?.legal_type === 'person' ? $t('bezeichnung_person') : $t('bezeichnung')"
                    >
                        <v-text-field
                            :value="addressToEdit?.title"
                            dense
                            outlined
                            hide-details="auto"
                            :placeholder="$t('eo_edit_title_placeholder')"
                            :counter="60"
                            :rules="
                                [
                                    addressToEdit?.legal_type === 'organization' ? rules.required : null,
                                    rules.maxLength(60),
                                ].filter(Boolean)
                            "
                            @input="addressToEdit?.set('title', $event)"
                        />
                    </LabelField>
                    <AddressIconBar :item="addressToEdit" hide-status-icon />
                </v-col>
            </v-row>

            <!-- Geschlecht -->
            <v-row v-if="addressToEdit?.legal_type === 'person'" dense>
                <v-col cols="12">
                    <LabelField :label="$t('anrede')">
                        <v-radio-group
                            dense
                            :value="addressToEdit?.sex"
                            row
                            @change="addressToEdit?.set('sex', $event)"
                        >
                            <v-radio :label="$t('frau')" value="f" />
                            <v-radio :label="$t('herr')" value="m" />
                            <v-radio :label="$t('divers')" value="d" />
                        </v-radio-group>
                    </LabelField>
                </v-col>
            </v-row>
            <!-- Vor-/Nachname -->
            <v-row v-if="addressToEdit?.legal_type === 'person'" dense>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('vorname')">
                        <v-text-field
                            :value="addressToEdit?.first_name"
                            dense
                            outlined
                            hide-details="auto"
                            :rules="[rules.required, rules.maxLength(100)]"
                            @input="addressToEdit?.set('first_name', $event)"
                        />
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('nachname')">
                        <v-text-field
                            :value="addressToEdit?.surname"
                            dense
                            outlined
                            hide-details="auto"
                            :rules="[rules.required, rules.maxLength(100)]"
                            @input="addressToEdit?.set('surname', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>

            <!-- Adresse -->
            <h4 class="mb-2 mt-5">{{ $t('adresse') }}</h4>
            <v-row dense>
                <v-col cols="12" lg="4" xl="5">
                    <LabelField :label="$t('strasse')">
                        <v-text-field
                            :value="addressToEdit?.street"
                            dense
                            outlined
                            hide-details="auto"
                            :rules="[rules.maxLength(100)].filter(Boolean)"
                            @input="addressToEdit?.set('street', $event)"
                        />
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="2" xl="1">
                    <LabelField :label="$t('strasse_nr')">
                        <v-text-field
                            :value="addressToEdit?.street_nr"
                            dense
                            outlined
                            hide-details="auto"
                            :rules="[rules.maxLength(15)]"
                            @input="addressToEdit?.set('street_nr', $event)"
                        />
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('zusatz')">
                        <v-text-field
                            :value="addressToEdit?.address_supplement"
                            dense
                            outlined
                            hide-details="auto"
                            :rules="[rules.maxLength(100)]"
                            @input="addressToEdit?.set('address_supplement', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>
            <!-- plz/zip, ort -->
            <v-row dense>
                <v-col cols="12" lg="6">
                    <ZipCityGroup :record="addressToEdit" />
                </v-col>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('postfach')">
                        <v-text-field
                            :value="addressToEdit?.po_box"
                            dense
                            outlined
                            hide-details="auto"
                            :rules="[rules.maxLength(100)]"
                            @input="addressToEdit?.set('po_box', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>
            <!-- land -->
            <v-row dense>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('land')">
                        <CountrySelectField
                            :value="addressToEdit?.country_id"
                            hide-details="auto"
                            item-value="id"
                            :label="null"
                            @input="addressToEdit?.set('country_id', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>

            <!-- EO specific -->
            <!-- nothing so far ... -->

            <h4 class="mt-5 mb-2">{{ $t('contact') }}</h4>
            <!-- tel -->
            <v-row dense>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('tel_1')">
                        <v-text-field
                            :value="addressToEdit?.tel"
                            dense
                            outlined
                            hide-details="auto"
                            :rules="[rules.maxLength(50)]"
                            @input="addressToEdit?.set('tel', $event)"
                        />
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('tel_2')">
                        <v-text-field
                            :value="addressToEdit?.mobile"
                            dense
                            outlined
                            hide-details="auto"
                            :rules="[rules.maxLength(50)]"
                            @input="addressToEdit?.set('mobile', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>
            <!-- mail -->
            <v-row dense>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('email')">
                        <v-text-field
                            :value="addressToEdit?.email"
                            dense
                            outlined
                            hide-details="auto"
                            :rules="[rules.email, rules.maxLength(255)]"
                            @input="addressToEdit?.set('email', $event)"
                        />
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('webseite')">
                        <v-text-field
                            :value="addressToEdit?.web"
                            dense
                            outlined
                            placeholder="https://..."
                            hide-details="auto"
                            :rules="[rules.maxLength(255)]"
                            @input="addressToEdit?.set('web', $event)"
                        />
                    </LabelField>
                </v-col>
            </v-row>
        </v-form>
    </v-container>
</template>

<script>
import { AddressCollection } from '@/models/Address';
import { EventOrganizer } from '@/models/EventOrganizer';
import { formRules } from '@/helpers/tools';
import CountrySelectField from '@/components/country/SelectField';
import { mapActions } from 'vuex';
import AddressIconBar from '@/components/address/AddressIconBar.vue';
import ZipCityGroup from '../address/ZipCityGroup.vue';

export default {
    name: 'EventOrganizerEditForm',
    components: {
        CountrySelectField,
        AddressIconBar,
        ZipCityGroup,
    },

    props: {
        eventOrganizer: {
            type: EventOrganizer,
            required: true,
        },
        writable: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            eoToEdit: null,
            addressToEdit: null,
            loading: true,
        };
    },

    watch: {
        eventOrganizer: {
            handler(newValue) {
                this.eoToEdit = newValue?.clone();
            },
            immediate: true,
        },
    },

    created() {
        this.rules = formRules;
    },

    async mounted() {
        if (this.eventOrganizer?.address_id) {
            this.addressToEdit = await this.loadAddressForId(this.eventOrganizer.address_id);
        }
        this.validateForm();
    },

    methods: {
        ...mapActions(['runAsync']),

        validateForm() {
            this.$refs.form?.validate();
        },

        async loadAddressForId(id) {
            if (!id) {
                return null;
            }
            const collection = new AddressCollection();
            await this.runAsync(async () => await collection.query({ id }));
            return collection.first();
        },

        async save() {
            const { addressChanged } = await this.saveAddress();
            this.eoToEdit.address_id = this.addressToEdit.getId();
            const { eoChanged } = await this.saveEo();

            if (eoChanged) {
                this.$emit('event-organizer-changed', {
                    eventOrganizer: this.eoToEdit,
                    address: this.addressToEdit,
                });
            } else if (addressChanged) {
                this.$emit('address-changed', {
                    eventOrganizer: this.eoToEdit,
                    address: this.addressToEdit,
                });
            }
        },

        async saveAddress() {
            const addressSaveNeeded = this.addressToEdit.isDirty();

            if (addressSaveNeeded) {
                await this.runAsync(async () => await this.addressToEdit.save());
            }
            return {
                addressChanged: addressSaveNeeded,
            };
        },

        async saveEo() {
            const eoSaveNeeded = this.eoToEdit?.isDirty();
            if (eoSaveNeeded) {
                await this.runAsync(async () => await this.eoToEdit.save());
            }
            return {
                eoChanged: eoSaveNeeded,
            };
        },
    },
};
</script>

<style lang="scss" scoped></style>
